import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Clock } from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';

const BLOG_POSTS = [
  {
    slug: 'travel-guide-bhusaval',
    title: 'Travel Guide: Bhusaval — Gateway to Ajanta & Ellora',
    excerpt:
      'Discover Bhusaval, one of the most important railway junctions in Maharashtra. Explore its rich history, vibrant Khandeshi cuisine, and proximity to the UNESCO World Heritage Ajanta and Ellora caves.',
    image: 'https://retiringroom.in/Bhusaval_Jn_Entrance.jpg',
    imageAlt: 'Scenic railway view near Bhusaval Junction, Maharashtra — a major rail hub in Central India',
    date: 'March 25, 2026',
    readTime: '8 min read',
    location: 'Maharashtra, India',
    tag: 'Maharashtra',
    tagColor: 'bg-railway-500',
  },
  {
    slug: 'travel-guide-kannur',
    title: 'Travel Guide: Kannur — Kerala\'s Cultural Crown',
    excerpt:
      'Explore Kannur, the land of looms and lores on Kerala\'s Malabar coast. From the spectacular Theyyam rituals to the pristine drive-in beach at Muzhappilangad, Kannur is an unforgettable destination.',
    image: 'https://upload.wikimedia.org/wikipedia/commons/f/f1/Fisherman_at_Muzhappilangad_Beach.jpg',
    imageAlt: 'Beautiful coastal landscape of Kannur, Kerala — known for pristine beaches and rich heritage',
    date: 'March 20, 2026',
    readTime: '10 min read',
    location: 'Kerala, India',
    tag: 'Kerala',
    tagColor: 'bg-green-600',
  },
  {
    slug: 'travel-guide-kottayam',
    title: 'Travel Guide: Kottayam — The Gateway to Backwaters',
    excerpt:
      'Kottayam, the City of Letters, Latex, and Lakes. Discover the serene backwaters of Kumarakom, ancient Syrian Christian churches, and the lush rubber plantations of Kerala\'s heartland.',
    image: 'https://upload.wikimedia.org/wikipedia/commons/4/4c/Vembanad_Lake_at_Kumarakom.jpg',
    imageAlt: 'Serene backwaters of Kumarakom near Kottayam, Kerala — a major hub for houseboat tourism',
    date: 'April 20, 2026',
    readTime: '12 min read',
    location: 'Kerala, India',
    tag: 'Kerala',
    tagColor: 'bg-green-600',
  },
];

const BLOG_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Retiring Room Travel Guides',
  description: 'Travel guides and tourism tips for cities connected through India\'s railway retiring room network.',
  url: 'https://retiringroom.in/blog',
  publisher: {
    '@type': 'Organization',
    name: 'Retiring Room',
    logo: {
      '@type': 'ImageObject',
      url: 'https://retiringroom.in/logo.png',
    },
  },
};

const Blog = () => {
  return (
    <PublicLayout>
      <SEO
        title="Travel Guides & Blog"
        description="Explore travel guides, tourism tips, and destination highlights for cities connected through India's railway retiring room network. Discover Bhusaval, Kannur, and more."
        canonical="/blog"
        keywords="travel guide India, Bhusaval travel, Kannur tourism, railway station travel tips, India tourism blog, Ajanta Ellora caves, Kerala beaches, Malabar coast"
        jsonLd={BLOG_JSON_LD}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-midnight-950 via-midnight-900 to-railway-950 text-white py-20">
        <div className="absolute inset-0 bg-railway-pattern opacity-20 pointer-events-none" aria-hidden="true"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-railway-400 font-semibold text-sm uppercase tracking-wider mb-3">Our Blog</span>
          <h1 className="text-4xl sm:text-5xl font-display font-bold">
            Travel Guides & Destination Tips
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto text-lg">
            Discover the hidden gems, rich culture, and must-visit attractions in cities connected through our retiring room network across India.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-gradient-to-b from-white to-midnight-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {BLOG_POSTS.map((post) => (
              <article key={post.slug} className="group">
                <Link to={`/blog/${post.slug}`} className="card hover:shadow-2xl transition-all duration-300 overflow-hidden block">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className={`inline-block ${post.tagColor} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                        {post.tag}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h2 className="text-xl font-display font-bold text-white leading-tight">
                        {post.title}
                      </h2>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-xs text-midnight-400 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                        {post.readTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                        {post.location}
                      </span>
                    </div>
                    <p className="text-midnight-600 text-sm leading-relaxed">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 text-railway-500 font-semibold text-sm group-hover:gap-2 transition-all">
                      Read Full Guide <ArrowRight className="w-4 h-4" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-midnight-900">
            Planning a Train Journey?
          </h2>
          <p className="mt-3 text-midnight-600">
            Book a comfortable retiring room at your destination station and start your trip refreshed.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 bg-railway-500 hover:bg-railway-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg shadow-railway-500/30"
          >
            Browse Available Stations
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Blog;
